import { collection, getDocs, query, where } from "firebase/firestore";

import { db } from "../firebase/firebase";
import { getAssignedClasses, getCurrentUser, isAdminUser } from "./auth";

export function toDate(value) {
  if (!value) return null;

  if (value?.toDate && typeof value.toDate === "function") {
    return value.toDate();
  }

  if (value instanceof Date) return value;

  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

export function parseIsoDate(value) {
  if (!value || typeof value !== "string") return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function mapUserDoc(docSnap) {
  const data = docSnap.data();

  return {
    id: docSnap.id,
    ...data,
    displayName:
      data.displayName || data.name || data.email || "Unbekannter User",
    className: data.className || "—",
    schoolName: data.schoolName || "—",
    email: data.email || "",
  };
}

export function getScopeMeta() {
  const currentUser = getCurrentUser();
  const assignedClasses = getAssignedClasses(currentUser);

  return {
    currentUser,
    isAdmin: isAdminUser(currentUser),
    assignedClasses,
    primaryClass: assignedClasses[0] || "",
    isScoped: !isAdminUser(currentUser),
  };
}

export async function fetchScopedUsers() {
  const { isAdmin, assignedClasses } = getScopeMeta();

  if (isAdmin) {
    const snap = await getDocs(collection(db, "users"));
    return snap.docs.map(mapUserDoc);
  }

  if (!assignedClasses.length) {
    return [];
  }

  const usersRef = collection(db, "users");

  const snap =
    assignedClasses.length === 1
      ? await getDocs(query(usersRef, where("className", "==", assignedClasses[0])))
      : await getDocs(query(usersRef, where("className", "in", assignedClasses.slice(0, 10))));

  return snap.docs.map(mapUserDoc);
}

export async function fetchScopedUserSubcollection(userId, subcollectionName) {
  const snap = await getDocs(collection(db, "users", userId, subcollectionName));
  return snap.docs.map((entry) => ({
    id: entry.id,
    ...entry.data(),
    userId,
  }));
}

export async function fetchScopedCollections({ includeJournal = false } = {}) {
  const users = await fetchScopedUsers();

  if (!users.length) {
    return {
      users: [],
      tasks: [],
      stressLogs: [],
      focusSessions: [],
      journal: [],
    };
  }

  const perUser = await Promise.all(
    users.map(async (user) => {
      const jobs = [
        fetchScopedUserSubcollection(user.id, "tasks"),
        fetchScopedUserSubcollection(user.id, "stressLogs"),
        fetchScopedUserSubcollection(user.id, "focusSessions"),
      ];

      if (includeJournal) {
        jobs.push(fetchScopedUserSubcollection(user.id, "journal"));
      }

      const [tasks, stressLogs, focusSessions, journal = []] = await Promise.all(jobs);

      return {
        tasks,
        stressLogs,
        focusSessions,
        journal,
      };
    })
  );

  return {
    users,
    tasks: perUser.flatMap((entry) => entry.tasks),
    stressLogs: perUser.flatMap((entry) => entry.stressLogs),
    focusSessions: perUser.flatMap((entry) => entry.focusSessions),
    journal: perUser.flatMap((entry) => entry.journal),
  };
}
